import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import type { PaymentRequestPdfModel } from "./paymentRequestPdfModel";

const BRAND = "#B03858";
const SLATE_800 = "#1e293b";
const SLATE_600 = "#475569";
const SLATE_500 = "#64748b";
const SLATE_200 = "#e2e8f0";
const SLATE_50 = "#f8fafc";

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingBottom: 48,
    paddingHorizontal: 44,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: SLATE_800,
    lineHeight: 1.45,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 2,
    borderBottomColor: BRAND,
  },
  logoBox: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  logoMark: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: BRAND,
    alignItems: "center",
    justifyContent: "center",
  },
  logoMarkText: {
    color: "#ffffff",
    fontSize: 14,
    fontWeight: 700,
  },
  logoTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: BRAND,
  },
  logoSubtitle: {
    fontSize: 8,
    color: SLATE_500,
    marginTop: 2,
  },
  docTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: SLATE_600,
    textAlign: "right",
    maxWidth: 200,
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 9,
    fontWeight: 700,
    color: BRAND,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: 8,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: SLATE_200,
  },
  row: {
    flexDirection: "row",
    marginBottom: 5,
  },
  label: {
    width: "38%",
    fontSize: 9,
    color: SLATE_500,
    fontWeight: 700,
  },
  value: {
    width: "62%",
    fontSize: 10,
    color: SLATE_800,
  },
  observacaoBox: {
    backgroundColor: SLATE_50,
    borderWidth: 1,
    borderColor: SLATE_200,
    borderRadius: 6,
    padding: 10,
    minHeight: 48,
  },
  observacaoText: {
    fontSize: 10,
    color: SLATE_800,
    lineHeight: 1.5,
  },
  bullet: {
    fontSize: 10,
    color: SLATE_800,
    marginBottom: 3,
    paddingLeft: 4,
  },
  footer: {
    position: "absolute",
    bottom: 24,
    left: 44,
    right: 44,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: SLATE_200,
  },
  footerText: {
    fontSize: 8,
    color: SLATE_500,
    marginBottom: 2,
  },
});

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.row}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{value}</Text>
    </View>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

interface Props {
  data: PaymentRequestPdfModel;
}

export function PaymentRequestPdfDocument({ data }: Props) {
  return (
    <Document title={`Solicitação de Pagamento — ${data.numeroSolicitacao}`}>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.logoBox}>
            <View style={styles.logoMark}>
              <Text style={styles.logoMarkText}>S</Text>
            </View>
            <View>
              <Text style={styles.logoTitle}>SEMOG</Text>
              <Text style={styles.logoSubtitle}>SEMOG BI — Gestão Integrada</Text>
            </View>
          </View>
          <Text style={styles.docTitle}>Resumo da Solicitação de Pagamento</Text>
        </View>

        <Section title="Dados da Solicitação">
          <FieldRow label="Solicitação" value={data.numeroSolicitacao} />
          <FieldRow label="Status" value={data.status} />
          <FieldRow label="Data de criação" value={data.dataCriacao} />
          <FieldRow label="Valor" value={data.valor} />
          <FieldRow label="Vencimento" value={data.vencimento} />
          <FieldRow label="Tipo documento" value={data.tipoDocumento} />
          <FieldRow label="Plano financeiro" value={data.planoFinanceiro} />
          <FieldRow label="Centro de custo" value={data.centroCusto} />
          <FieldRow label="Fornecedor" value={data.fornecedor} />
          <FieldRow label="Solicitante" value={data.solicitante} />
        </Section>

        <Section title="Dados Financeiros">
          <FieldRow label="Valor total" value={data.valor} />
          <FieldRow label="Forma de pagamento" value={data.formaPagamento} />
          <FieldRow label="Data de vencimento" value={data.vencimento} />
        </Section>

        <Section title="Dados Bancários">
          {data.dadosBancarios.map((row) => (
            <FieldRow key={row.label} label={row.label} value={row.value} />
          ))}
        </Section>

        <Section title="Aprovação">
          <FieldRow label="Aprovador operacional" value={data.aprovadorOperacional} />
          {data.dataAprovacaoOperacional && (
            <FieldRow label="Data da aprovação" value={data.dataAprovacaoOperacional} />
          )}
        </Section>

        <Section title="Observação">
          <View style={styles.observacaoBox}>
            <Text style={styles.observacaoText}>{data.observacao}</Text>
          </View>
        </Section>

        <Section title="Anexos">
          {data.anexos.length === 0 ? (
            <Text style={styles.bullet}>Nenhum anexo vinculado.</Text>
          ) : (
            data.anexos.map((name) => (
              <Text key={name} style={styles.bullet}>
                • {name}
              </Text>
            ))
          )}
        </Section>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>Documento emitido em: {data.emitidoEm}</Text>
          <Text style={styles.footerText}>Gerado por: {data.geradoPor}</Text>
        </View>
      </Page>
    </Document>
  );
}
